import type { Lang } from '../lib/types';
import type { DataClassRecord, DataFieldRecord, FieldDef, OwnedField, Values, Visibility } from './types';

// Pure schema helpers for game data. allFields/defaultVisibility mirror pocketbase/pb_hooks/lib/
// data-merge.js; schema.test.ts keeps both in step.

type ClassLike = { base?: string; fields?: FieldDef[] | null };

/** The class and its base classes, most derived first. */
export function classChain(classes: Record<string, ClassLike>, className: string): string[] {
  const out: string[] = [];
  for (
    let c: string | undefined = className;
    c && classes[c] && !out.includes(c);
    c = classes[c].base || undefined
  )
    out.push(c);
  return out;
}

/** Every serialized field of a class, base class first (Unity's order), with the declaring owner. */
export function allFields(classes: Record<string, ClassLike>, className: string): OwnedField[] {
  return classChain(classes, className)
    .reverse()
    .flatMap((c) => (classes[c].fields ?? []).map((f) => ({ owner: c, ...f })));
}

/** Can a record of `className` be put into a ref field whose target type is `target`? */
export function isAssignable(classes: Record<string, ClassLike>, className: string, target: string): boolean {
  return classChain(classes, className).includes(target);
}

/** Mirrors data-merge.js defaultVisibility: used until the import has created a settings row. */
/** Mirrors TUNING_NAME / ID_NAME in data-merge.js (Unity tuning starts hidden, numeric/enum ids read-only). */
const TUNING_NAME =
  /^(vfx|sfx|sound|audio|animator|grip|stow|hide|covers)|anchor|mesh|material|clip|prefab|offset|shader|particle|socket|fitweight|animation(trigger|leadtime)$/i;
const ID_NAME = /[a-z]Id$/;

export function defaultVisibility(f: FieldDef | undefined): Visibility {
  if (!f || f.hideInInspector) return 'hidden';
  if (f.name && TUNING_NAME.test(f.name)) return 'hidden';
  if (f.name && ID_NAME.test(f.name) && (f.kind === 'int' || f.kind === 'enum')) return 'readonly';
  switch (f.kind) {
    case 'asset':
      return f.assetType === 'Sprite' ? 'readonly' : 'hidden';
    case 'vector':
    case 'color':
    case 'unsupported':
      return 'hidden';
    case 'list':
      return defaultVisibility(f.element);
    default:
      return 'edit';
  }
}

export const settingKey = (owner: string, name: string) => `${owner}|${name}`;

export function visibilityOf(f: OwnedField, settings: Record<string, DataFieldRecord>): Visibility {
  return settings[settingKey(f.owner, f.name)]?.visibility ?? defaultVisibility(f);
}

/** "TalkToNPC" → "Talk To NPC", "Talk_1" → "Talk 1", "maxHealth" → "Max Health". */
export function humanize(s: string): string {
  return s
    .replace(/^m_/, '')
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

export function fieldLabel(f: OwnedField, setting: DataFieldRecord | undefined, lang: Lang | string): string {
  const custom = lang === 'sv' ? setting?.labelSv || setting?.labelEn : setting?.labelEn || setting?.labelSv;
  return custom || f.label || humanize(f.name);
}

export function fieldHelp(f: OwnedField, setting: DataFieldRecord | undefined, lang: Lang | string): string {
  const custom = lang === 'sv' ? setting?.helpSv || setting?.helpEn : setting?.helpEn || setting?.helpSv;
  return custom || f.tooltip || '';
}

export interface Section {
  title: string;
  fields: OwnedField[];
}

/**
 * Groups fields like Unity's inspector: a [Header] starts a section that runs until the next one;
 * an admin-set section name overrides the header for that one field.
 */
export function sectionsOf(fields: OwnedField[], settings: Record<string, DataFieldRecord>): Section[] {
  const out: Section[] = [];
  let current = '';
  for (const f of fields) {
    if (f.header) current = f.header;
    const title = settings[settingKey(f.owner, f.name)]?.section || current;
    let s = out.find((x) => x.title === title);
    if (!s) out.push((s = { title, fields: [] }));
    s.fields.push(f);
  }
  return out;
}

/** Enum values in declaration order, from the class table (kind 'enum'). */
export function enumValues(classes: Record<string, DataClassRecord>, type: string | undefined) {
  return (type && classes[type]?.enumValues) || [];
}

/**
 * Every data-asset reference inside a value, with the top-level field it sits in: {guid} with no fileID
 * (assets are {guid, fileID}), or {new: recordId} for assets created on the web, reported as "new:<id>".
 */
export function collectRefs(values: Values | null): { field: string; guid: string }[] {
  const out: { field: string; guid: string }[] = [];
  const walk = (v: unknown, field: string) => {
    if (Array.isArray(v)) v.forEach((x) => walk(x, field));
    else if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      if (typeof o.guid === 'string' && o.fileID === undefined) out.push({ field, guid: o.guid });
      else if (typeof o.new === 'string' && Object.keys(o).length === 1)
        out.push({ field, guid: 'new:' + o.new });
      else for (const k of Object.keys(o)) walk(o[k], field);
    }
  };
  for (const [k, v] of Object.entries(values ?? {})) walk(v, k);
  return out;
}

/**
 * Fields hidden by the class's showWhen for these values (e.g. an objective's enemyTag unless it is a
 * Kill). `root` is the whole asset, for parent flags. Returns null when every field applies.
 */
export function hiddenByCondition(
  classes: Record<string, DataClassRecord>,
  className: string,
  values: Values | null | undefined,
  root: Values | null | undefined,
): Set<string> | null {
  const sw = classes[className]?.showWhen;
  if (!sw) return null;
  const hidden = new Set<string>();
  const allowed = sw.fields[String(values?.[sw.field] ?? '')];
  if (allowed)
    for (const f of allFields(classes, className)) if (!allowed.includes(f.name)) hidden.add(f.name);
  for (const [field, flag] of Object.entries(sw.parentFlags ?? {}))
    if (root?.[flag] !== true) hidden.add(field);
  return hidden;
}

/**
 * How a text field is filled in when the admin left "Input" on automatic: fields that name places on the
 * map pick a map feature's Unity id; code-bound names pick from the values Unity already uses.
 */
const AUTO_PICKERS: Record<string, string> = {
  mapZoneId: 'map:zone',
  mapPoiId: 'map:poi',
  targetLocationTag: 'map:quest_location',
  enemyTag: 'vocab',
  customEventKey: 'vocab',
  harvestTypeId: 'vocab',
  // NPC names in quests/dialogue ("Erik") need not match map feature names ("Elder Erik"): suggest the
  // names the game already uses; "NPC on the map" can still be chosen under Admin → Fields.
  questGiverNPC: 'vocab',
  turnInNPC: 'vocab',
  targetNPCName: 'vocab',
  npcName: 'vocab',
  category: 'vocab',
  journalRegion: 'vocab',
};

export function pickerFor(fieldName: string, setting: DataFieldRecord | undefined): string {
  const p = setting?.picker ?? '';
  if (p === 'free') return '';
  return p || AUTO_PICKERS[fieldName] || '';
}

/** Numbers as the game data stores them: always '.', never locale formatting (see decimal-comma memory). */
export function formatNumber(v: unknown): string {
  if (typeof v !== 'number' || !isFinite(v)) return '—';
  return String(Number(v.toPrecision(7)));
}
